import { Injectable, OnDestroy } from '@angular/core';
import { IDeviceNotificationModel } from '@notifications/abstract/device-notification-model';
import { INotificationAvailableDevice } from '@notifications/abstract/notification-available-device';
import { IRefreshedNotificationItem } from '@notifications/abstract/refreshed-notification-item';
import { RefreshNotificationItemAction } from '@notifications/constants/refresh-notification-item-action';
import { IPageModel } from '@shared/interfaces/page-model';
import { ApiService } from '@shared/services/api.service';
import { DevicePublisherService } from '@shared/services/publishers/device-publisher.service';
import { Observable, Subject, Subscription, scan, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DeviceNotificationService implements OnDestroy {
  deviceNotifications$: Observable<IDeviceNotificationModel[]>;
  unreadDeviceNotificationCount$ = new Subject<number>();

  private subscriptions = new Subscription();

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  constructor(
    private readonly api: ApiService,
    private readonly devicePublisherService: DevicePublisherService
  ) {
    this.deviceNotifications$ = this.devicePublisherService
      .getDeviceMessageObservable()
      .pipe(
        scan(
          (
            acc: IDeviceNotificationModel[],
            current: IRefreshedNotificationItem<IDeviceNotificationModel>
          ) => {
            switch (current.action) {
              case RefreshNotificationItemAction.Prepend:
                this.subscriptions.add(this.getUnreadCount().subscribe());
                return [current.value, ...acc];
              case RefreshNotificationItemAction.Append:
                return [...acc, current.value];
              case RefreshNotificationItemAction.ClearAll:
                return [];
              case RefreshNotificationItemAction.ClearItem:
                return acc.filter(
                  (wn: IDeviceNotificationModel) => wn.id !== current.value.id
                );
              default:
                return acc;
            }
          },
          [] as IDeviceNotificationModel[]
        )
      );
  }

  getPage(
    pageSize = 10,
    pageNumber = 0,
    deviceId: string | null = null
  ): Observable<IPageModel<IDeviceNotificationModel>> {
    return this.api
      .getUserDeviceNotifications(pageSize, pageNumber, deviceId)
      .pipe(
        tap((response: IPageModel<IDeviceNotificationModel>) => {
          response.data?.forEach((item) => {
            this.devicePublisherService.getDeviceMessageObservable().next({
              action: RefreshNotificationItemAction.Append,
              value: item,
            } as IRefreshedNotificationItem<IDeviceNotificationModel>);
          });
        })
      );
  }

  clearNotification(
    deviceNotification: IDeviceNotificationModel
  ): Observable<void> {
    return this.api.deleteUserDeviceNotifications([deviceNotification.id]).pipe(
      tap((_) => {
        this.devicePublisherService.getDeviceMessageObservable().next({
          action: RefreshNotificationItemAction.ClearItem,
          value: deviceNotification,
        } as IRefreshedNotificationItem<IDeviceNotificationModel>);
      })
    );
  }

  clearAll(deviceNotifications: IDeviceNotificationModel[]): Observable<void> {
    return this.api
      .deleteUserDeviceNotifications(deviceNotifications.map((x) => x.id))
      .pipe(
        tap((_) => {
          this.devicePublisherService.getDeviceMessageObservable().next({
            action: RefreshNotificationItemAction.ClearAll,
            value: {} as IDeviceNotificationModel,
          } as IRefreshedNotificationItem<IDeviceNotificationModel>);
        })
      );
  }

  clearArrayOfUserDeviceNotifications(){
    this.devicePublisherService.getDeviceMessageObservable().next({
      action: RefreshNotificationItemAction.ClearAll,
      value: {} as IDeviceNotificationModel,
    } as IRefreshedNotificationItem<IDeviceNotificationModel>);
  }

  getUnreadCount(): Observable<number> {
    return this.api.getUnreadUserDeviceNotificationsCount().pipe(
      tap((response: number) => {
        this.unreadDeviceNotificationCount$.next(response);
      })
    );
  }

  markAsRead(
    deviceNotifications: IDeviceNotificationModel[]
  ): Observable<void> {
    return this.api.markAsReadUserDeviceNotifications(
      deviceNotifications.map((x) => x.id)
    );
  }

  getAvailableDevices(): Observable<INotificationAvailableDevice[]> {
    return this.api.getUserNotificationAvailableDevices();
  }
}
